import * as Sentry from '@sentry/browser';
import { FAST_API_URL } from './apiConstants';
import TokenService from '../services/token-service';

const dsn = process.env.SENTRY_DSN;
const isProduction = process.env.NODE_ENV === 'production';
const isSentryEnabled = Boolean(dsn) && isProduction;

/**
 * Update Sentry user context and tags.
 * Call this after login, logout, or any auth state change.
 */
export function updateSentryUserContext() {
  if (!isSentryEnabled) return;

  const user = TokenService.getUser();
  const clientName = localStorage.getItem('client_name');
  const configName = localStorage.getItem('config_name');

  if (user) {
    // Set user context
    Sentry.setUser({
      id: String(user.user_id),
      username: user.user_name,
    });

    // Set tags in a specific order for consistent display
    // 1. User identification
    Sentry.setTag('user_id', String(user.user_id));
    Sentry.setTag('username', user.user_name);

    // 2. Client information
    Sentry.setTag('client_id', String(user.user_client_id));
    if (clientName) {
      Sentry.setTag('client_name', clientName);
    }

    // 3. Configuration
    if (configName) {
      Sentry.setTag('config_name', configName);
    }
    if (user.config_name) {
      Sentry.setTag('user_config_name', user.config_name);
    }

    // 4. User details
    if (user.user_type) {
      Sentry.setTag('user_type', user.user_type);
    }
    if (user.user_email) {
      Sentry.setTag('user_email', user.user_email);
    }

    // 5. Environment tag
    Sentry.setTag('environment', isProduction ? 'production' : 'development');
  } else {
    // Clear user context on logout
    Sentry.setUser(null);
  }
}

/**
 * Send a manually reported error to GlitchTip (Sentry-compatible).
 * No-op when Sentry is not initialized (non-production or missing DSN).
 */
export function captureReportedError(
  message: string,
  error: unknown,
  errorType: string,
  context?: Record<string, unknown>,
): void {
  if (!isSentryEnabled) return;

  const underlyingMessage =
    error instanceof Error
      ? error.message
      : ((error as { message?: string })?.message ??
        (error != null ? String(error) : 'Unknown error'));

  const captureError =
    error instanceof Error ? error : new Error(underlyingMessage);

  if (!(error instanceof Error)) {
    captureError.message = `${message}: ${underlyingMessage}`;
  }

  Sentry.withScope((scope) => {
    scope.setTag('error_type', errorType);
    scope.setExtra('report_message', message);

    if (context && Object.keys(context).length > 0) {
      scope.setContext('reported_error', context);
    }

    if (context?._debounced) {
      scope.setTag('debounced', 'true');
      if (context._count != null) {
        scope.setExtra('occurrence_count', context._count);
      }
    }

    Sentry.captureException(captureError);
  });
}

// Only initialize Sentry in production
if (isSentryEnabled) {
  try {
    Sentry.init({
      dsn,
      environment: 'production',
      // IMPORTANT: tunnel should be same-origin if you can.
      // If it's cross-origin, you'll need CORS on the backend.
      tunnel: `${FAST_API_URL}/v1/errors/glitchtip-proxy`,

      // Disable BrowserSession integration sentry v9.0.0+ - future proofing
      integrations: function (integrations) {
        return integrations.filter(function (integration) {
          return integration.name !== 'BrowserSession';
        });
      },
      // We are currently using "@sentry/browser": "^8.0.0",
      // so we need to disable auto session tracking
      autoSessionTracking: false,
      // We don't want to send client reports to Sentry.
      // 501 errors occure due to our version of GlitchTip, client_report  envelope type not supported.
      sendClientReports: false,
    });

    // Set user context and tags on initialization
    updateSentryUserContext();
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
}
